"use client";
import { useState } from "react";
import { useTranslation } from "../contexts/I18nProvider";
import HomeLayout, { Logo } from "../layouts/home";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useHashScroll } from "../hooks/useHashScroll";
import {
  ArrowRight,
  Video,
  BookOpen,
  Computer,
  Lock,
  Filter,
  Search,
  VideoIcon,
  AlertCircle,
} from "lucide-react"; // Lucide React Icons
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { API_BASE_URL } from "../lib/constants";

import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { ToastAction } from "../components/ui/toast";
import contactSchema from "../schemas/contactSchema";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PricingSection } from "./Landing/PricingSection";


function Landing() {
  useHashScroll();
  
  return (
    <HomeLayout>
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <ContactSection />
      <FooterSection />
    </HomeLayout>
  );
}

export const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="min-h-[calc(100dvh_-_350px)] md:min-h-[calc(100dvh_-_320px)] flex items-center bg-white dark:bg-slate-950">
      <div className="py-6 md:py-10 lg:py-12 px-4 mx-auto max-w-screen-xl text-center lg:px-12 group">

        {/* Main Heading */}
        <h1 className="mb-4 text-4xl font-extrabold tracking-tight leading-none text-[#333333] md:text-5xl lg:text-6xl dark:text-white">
          {(() => {
            const title = t('landing.hero.title');
            const highlight = t('landing.hero.highlight');
            const parts = title.split(highlight);
            return parts.length === 2 ? (
              <>
                {parts[0]}
                <span className="px-1 bg-gradient-to-r from-[#1a535c] via-[#4ecdc4] via-[#d35400] via-[#b02e0c] via-[#6d6875] to-[#264653] text-transparent bg-clip-text">
                  {highlight}
                </span>
                {parts[1]}
              </>
            ) : (
              title
            );
          })()}
        </h1>

        {/* Description */}
        <p className="mb-8 text-lg font-normal text-[#333333fd] lg:text-xl sm:px-16 xl:px-48 dark:text-white">
          {t('landing.hero.description')}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col mb-8 lg:mb-16 space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
          <Button onClick={() => navigate("/polyglot-legacy")} className="py-3 px-5 h-[50px] text-base font-medium">
            {t('landing.hero.cta')} <ArrowRight />
          </Button>

          {/* Dialog for Demo Videos */}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="py-3 px-5 h-[50px] text-base hover:bg-primary/5 font-medium">
                {t('landing.hero.demo')} <Video />
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-xl overflow-hidden">
              <DialogHeader>
                <DialogTitle className="flex gap-2 items-center">
                  <VideoIcon size={16} /> {t('landing.hero.demoTitle')}
                </DialogTitle>
                <DialogDescription>
                  {t('landing.hero.demoSubtitle')}
                </DialogDescription>

                {/* Tabs for Video Selection */}
                <Tabs defaultValue="video1" className="mt-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="video1">{t('landing.hero.demoTab1')}</TabsTrigger>
                    <TabsTrigger value="video2">{t('landing.hero.demoTab2')}</TabsTrigger>
                    <TabsTrigger value="video3">{t('landing.hero.demoTab3')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="video1">
                    <iframe
                      className="rounded-sm mt-2 w-full"
                      title="Intro Video"
                      width="100%"
                      height="230"
                      src="https://www.youtube.com/embed/4VNRN6IjveM?vq=hd1080"
                      allow="fullscreen"
                    ></iframe>
                  </TabsContent>

                  <TabsContent value="video2">
                    <iframe
                      className="rounded-sm mt-2 w-full"
                      title="Feature Demo"
                      width="100%"
                      height="230"
                      src="https://www.youtube.com/embed/Bys5I5VvBDg?vq=hd1080"
                      allow="fullscreen"
                    ></iframe>
                  </TabsContent>

                  <TabsContent value="video3">
                    <iframe
                      className="rounded-sm mt-2 w-full"
                      title="Tutorial"
                      width="100%"
                      height="230"
                      src="https://www.youtube.com/embed/PJHfyCqjDh4?vq=hd1080"
                      allow="fullscreen"
                    ></iframe>
                  </TabsContent>
                </Tabs>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};

export const FeaturesSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <BookOpen size={32} className="text-primary" />,
      title: t('landing.features.interactiveLessons'),
      description: t('landing.features.interactiveDescription')
    },
    {
      icon: <Search size={32} className="text-primary" />,
      title: t('landing.features.instantTranslation'),
      description: t('landing.features.translationDescription')
    },
    {
      icon: <Filter size={32} className="text-primary" />,
      title: t('landing.features.personalizedLearning'),
      description: t('landing.features.personalizedDescription')
    },
    {
      icon: <Lock size={32} className="text-primary" />,
      title: t('landing.features.securePrivate'),
      description: t('landing.features.secureDescription')
    },
    {
      icon: <Computer size={32} className="text-primary" />,
      title: t('landing.features.learnAnywhere'),
      description: t('landing.features.learnAnywhereDescription')
    },
    {
      icon: <Video size={32} className="text-primary" />,
      title: t('landing.features.richMediaContent'),
      description: t('landing.features.mediaDescription')
    },
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-white -mt-6 md:-mt-8 lg:-mt-10">
      <div className="max-w-screen-xl px-4 mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="mb-4 text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#333333]">
            {t('landing.features.title')}
          </h2>
          <p className="text-lg text-[#333333ef] sm:px-16 xl:px-32">
            {t('landing.features.subtitle')}
          </p>
        </div>

        {/* Feature Cards Grid - Responsive: 1 col mobile, 2 cols tablet, 3 cols desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-6 md:p-8 bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-bold text-[#333333] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-[#333333ef] text-sm md:text-base">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ContactSection = ({ noImage }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form data
    const validation = contactSchema.safeParse(formData);
    if (!validation.success) {
      const errorMessages = validation.error.format();
      setErrors({
        email: errorMessages.email?._errors[0],
        subject: errorMessages.subject?._errors[0],
        message: errorMessages.message?._errors[0],
      });
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(
        API_BASE_URL + "/api/contact-request/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      if (response.ok) {
        toast({
          title: t('landing.contact.success'),
          description: t('landing.contact.successDescription'),
        });
        setFormData({ email: "", subject: "", message: "" });
      } else {
        toast({
          variant: "destructive",
          title: t('landing.contact.error'),
          description: t('landing.contact.errorDescription'),
          action: <ToastAction altText="Try again">Try again</ToastAction>,
        });
      }
    } catch (error) {
      toast({
        title: t('landing.contact.networkError'),
        description: t('landing.contact.networkErrorDescription'),
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  return (
    <section className="dark:bg-white flex items-center justify-between flex-col lg:flex-row gap-2 lg:gap-6">
      {!noImage && (
        <img
          className="max-h-[500px] rounded-3xl"
          src={
            API_BASE_URL +
            "/frontend_assets/pexels-bestbe-models-975242-2170387.webp"
          }
          alt="girls"
        />
      )}
      <div className="py-8 lg:py-16 px-4 mx-auto max-w-screen-md">
        <h2 className="mb-4 text-4xl tracking-tight font-bold text-center text-primary dark:text-white">
          {t('landing.contact.title')}
        </h2>
        <p className="mb-8 lg:mb-16 font-light text-center text-text-secondary dark:text-white sm:text-xl">
          {t('landing.contact.subtitle')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-6 bg-white/95 border border-border/40 rounded-2xl p-8 shadow-sm">
          <div>
            <Label
              htmlFor="email"
              className="block mb-2 text-sm font-semibold text-text dark:text-white"
            >
              {t('landing.contact.emailLabel')}
            </Label>
            <Input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('landing.contact.emailPlaceholder')}
              className={`rounded-lg border-gray-200 dark:border-gray-700 ${
                errors.email ? "border-red-500 focus:ring-red-500" : "border-border focus:ring-primary"
              }`}
              required
            />
            {errors.email && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1 mt-2">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>
          <div>
            <Label
              htmlFor="subject"
              className="block mb-2 text-sm font-semibold text-text dark:text-white"
            >
              {t('landing.contact.subjectLabel')}
            </Label>
            <Input
              type="text"
              id="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder={t('landing.contact.subjectPlaceholder')}
              className={`rounded-lg border-gray-200 dark:border-gray-700 ${
                errors.subject ? "border-red-500 focus:ring-red-500" : "border-border focus:ring-primary"
              }`}
              required
            />
            {errors.subject && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1 mt-2">
                <AlertCircle className="w-4 h-4" />
                {errors.subject}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label
              htmlFor="message"
              className="block mb-2 text-sm font-semibold text-text dark:text-white"
            >
              {t('landing.contact.messageLabel')}
            </Label>
            <Textarea
              id="message"
              rows="6"
              value={formData.message}
              onChange={handleChange}
              placeholder={t('landing.contact.messagePlaceholder')}
              className={`rounded-lg border-gray-200 dark:border-gray-700 ${
                errors.message ? "border-red-500 focus:ring-red-500" : "border-border focus:ring-primary"
              }`}
            ></Textarea>
            {errors.message && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1 mt-2">
                <AlertCircle className="w-4 h-4" />
                {errors.message}
              </p>
            )}
          </div>
          <Button
            size="lg"
            type="submit"
            className="w-full rounded-lg bg-primary hover:bg-primary-dark text-white font-semibold"
            disabled={loading}
          >
            {loading ? t('landing.contact.sending') : t('landing.contact.sendButton')}
          </Button>
        </form>
      </div>
      <Toaster />
    </section>
  );
};

export const FooterSection = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-white border-t border-gray-200 py-12 px-4 md:px-8 lg:px-10">
      <div className="mx-auto max-w-screen-xl">
        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand Section */}
          <div className="flex flex-col items-start">
            <Logo />
            <p className="mt-4 text-sm text-gray-600">
              {t('landing.footer.brandDescription')}
            </p>
          </div>

          {/* Navigation Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('landing.footer.navigationTitle')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.home')}
                </Link>
              </li>
              <li>
                <Link to="/polyglot" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.polyglot')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.pricing')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.contact')}
                </Link>
              </li>
              <li>
                <Link to="/help" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.help')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & Resources Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('landing.footer.legalTitle')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/terms" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.termsOfService')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <a href="https://africanlanguagelibrary.tchamna.com/" target="_blank" rel="noreferrer" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.africanLibrary')}
                </a>
              </li>
              <li>
                <Link to="/get-started" className="text-sm text-gray-600 hover:text-primary transition-colors">
                  {t('landing.footer.getStarted')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 pt-8">
          {/* Copyright */}
          <p className="text-sm text-gray-600 text-center">
            © 2025 <span className="font-semibold text-gray-900">The African Polyglot</span>. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Landing;
